import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import Bowser from "bowser";

import { checkCodeExists } from "components/helper/callApi";
import Layout from "components/common/Layout";
import { ValidateButton } from "components/common/ValidateButton";
import { Input } from "components/common/Input";
import ScanQRCodeButton from "components/qrCodeScanner/ScanQRCodeButton";

import { getCustomizationLocalStorage } from "services/customizationService";

import { CSS_VARIABLES } from "enums/cssVariables";
import { ERROR } from "types/snackBar.constant";

type Inputs = {
    sessionCode: string;
};

const HomePage = () => {
    const {
        register,
        handleSubmit,
        setValue,
        setError,
        resetField,
        formState: { errors },
    } = useForm<Inputs>({
        mode: "onTouched",
    });

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const isBrowserSupported = useMemo(() => {
        const userAgent = window.navigator.userAgent;
        const browser = Bowser.getParser(userAgent);
        return userAgent
            ? browser.satisfies({
                  chrome: ">=16",
                  firefox: ">=11",
                  safari: ">=5",
                  edge: ">=12",
                  ie: ">=10",
                  "chrome mobile": ">=16",
                  "firefox mobile": ">=11",
                  "safari mobile": ">=5",
                  opera: ">=12.1",
                  "opera mobile": ">=12.1",
                  "samsung internet": ">=4",
              })
            : true;
    }, []);

    useEffect(() => {
        if (!isBrowserSupported) {
            enqueueSnackbar(
                <>
                    {t("outdatedBrowser1", {
                        product:
                            window.env.REACT_APP_THEME === "votebox"
                                ? "Votebox"
                                : "Quizzbox",
                    })}
                    <br />
                    {t("outdatedBrowser2")}
                    <br />
                    {t("outdatedBrowser3")}
                </>,
                {
                    variant: ERROR,
                    persist: true,
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBrowserSupported]);

    const isCustomization = Boolean(getCustomizationLocalStorage());
    const showQRCodeScanner = isMobile;

    const onQRCodeScanned = (value: string) => {
        let sessionCode = value;
        if (sessionCode.includes("/")) {
            const parts = sessionCode.split("/");
            sessionCode = parts[parts.length - 1];
        }
        setValue("sessionCode", sessionCode.toUpperCase());
    };

    const onSubmit = async (inputs: Inputs) => {
        try {
            const res = await checkCodeExists(inputs.sessionCode);

            if (res.ok) {
                resetField("sessionCode");
                navigate(`/login/${inputs.sessionCode}`);
            } else {
                enqueueSnackbar(t(`errorCode.${window.env.REACT_APP_THEME}`), {
                    variant: ERROR,
                });
                setError("sessionCode", {
                    type: "manual",
                });
            }
        } catch {
            enqueueSnackbar(`${t("errorUnknown")}`, {
                variant: ERROR,
            });
        }
    };

    return (
        <>
            <Layout>
                <Wrapper>
                    <FormWrapper>
                        <FormTitle
                            $isCustomization={isCustomization}
                        >{`${t("welcome")}`}</FormTitle>
                        <FormContent>
                            <p>
                                {t(`enterCode.${window.env.REACT_APP_THEME}`)}
                            </p>
                            <div>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <InputWithQRCodeWrapper>
                                        <StyledInput
                                            {...register("sessionCode")}
                                            $error={Boolean(errors.sessionCode)}
                                            placeholder={t(
                                                `codeOnline.${window.env.REACT_APP_THEME}`,
                                            )}
                                            aria-invalid={
                                                errors.sessionCode
                                                    ? "true"
                                                    : "false"
                                            }
                                            onChange={(
                                                e: React.FormEvent<HTMLInputElement>,
                                            ) => {
                                                const value =
                                                    e.currentTarget.value || "";
                                                setValue(
                                                    "sessionCode",
                                                    value.toUpperCase(),
                                                );
                                            }}
                                            dir={i18n.dir(i18n.language)}
                                            style={{
                                                paddingRight: showQRCodeScanner
                                                    ? "35px"
                                                    : "12px",
                                            }}
                                        />
                                        {showQRCodeScanner && (
                                            <QRCodeButtonWrapper>
                                                <ScanQRCodeButton
                                                    setValue={onQRCodeScanned}
                                                />
                                            </QRCodeButtonWrapper>
                                        )}
                                    </InputWithQRCodeWrapper>
                                    <StyledButton
                                        type="submit"
                                        disabled={!isBrowserSupported}
                                    >
                                        {`${t("joinIn")}`}
                                    </StyledButton>
                                </form>
                            </div>
                        </FormContent>
                    </FormWrapper>
                </Wrapper>
            </Layout>
        </>
    );
};

interface StyledIsCustomizationProps {
    $isCustomization: boolean;
}

const Wrapper = styled.div`
    padding: 0 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const FormWrapper = styled.div`
    text-align: center;
    background-color: #fafafa;
    box-shadow: 0px 0px 3px 0px rgb(0 0 0 / 35%);
    width: 320px;
`;

const FormTitle = styled.div<StyledIsCustomizationProps>`
    font-size: 1.3rem;
    padding: 1.25rem 1rem;
    color: ${(props) =>
        props.$isCustomization
            ? `var(${CSS_VARIABLES.BACKGROUND_FORM_COLOR})`
            : `var(${CSS_VARIABLES.PRIMARY_TEXT_COLOR})`};
`;

const FormContent = styled.div`
    padding: 0 1.5rem 0.5rem 1.5rem;
    > p {
        font-size: 0.9rem;
        line-height: 1.5rem;
        margin: 0;
        padding-bottom: 1rem;
    }
    > div {
        margin-bottom: 15px;
    }
`;

const InputWithQRCodeWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const QRCodeButtonWrapper = styled.div`
    position: absolute;
    right: 0;
    top: 0;
`;

const StyledInput = styled(Input)<{ $error: boolean }>`
    border-color: ${(props) => (props.$error ? "#dc3545" : null)};
    box-shadow: ${(props) =>
        props.$error ? "0 0 0 0.2rem rgb(220 53 69 / 25%)" : null};
    &:focus {
        border-color: ${(props) => (props.$error ? "#dc3545" : null)};
        box-shadow: ${(props) =>
            props.$error ? "0 0 0 0.2rem rgb(220 53 69 / 25%)" : null};
    }
`;

const StyledButton = styled(ValidateButton)``;

export default HomePage;
